/**
 * Scene Editor — Version Settings hierarchy (outliner).
 *
 * Components: base.predefinedGameComponents + parentKey nesting.
 * Category layout is read from gameConfig.metadata.categoryData when this
 * version has been edited; otherwise the default version or base sections.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import _ from "lodash";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import FolderIcon from "@material-ui/icons/Folder";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import InnerComponentIcon from "../../../images/gameConfig/Inner-component-icon.svg";
import {
  normalizeCategoryName,
  validateNewCategoryName,
  validateRenameCategoryName,
  renameCategoryInMeta,
  deleteCategoryFromMeta,
} from "../../modules/playEngineComponentCategoryUtils";
import {
  ROOT_LAYOUT_SCOPE,
  orderKeyForCategory,
  orderKeyForParent,
  rootLayoutEntryForComponent,
  rootLayoutEntryForFolder,
  parseRootLayoutEntry,
  resolveRootLayoutOrder,
  remapRootLayoutOrder,
  pruneRootLayoutOrder,
  sortSiblings,
} from "../../modules/playEngineComponentOrderUtils";
import { computePreviewIdsFromPointer } from "../../modules/playEngineComponentListFlip";
import {
  extractSceneEditorCategoryData,
  hasOwnSceneEditorCategoryData,
  findDefaultVersionRecord,
  resolveEffectiveCategoryData,
  writeSceneEditorCategoryData,
} from "../../modules/sceneEditorMetadataFromSections";
import {
  makeSceneEditorGroupItemId,
  makeSceneEditorSubGroupItemId,
} from "./sceneEditorHierarchySelection";

const PANEL_BG = "#2b2b2e";
const BAR_BG = "#3a3a41";
const CARD_BG = "#474747";
const SECTION_DIVIDER = "#3a3a3f";
/** Scene-editor category folders (PlayEngine keeps amber `#c9a25b`). */
const FOLDER_ICON_COLOR = "#adadad";

const TREE_SX = {
  overflowX: "hidden",
  flexGrow: 1,
  "& .MuiTreeItem-content": {
    padding: "0 4px",
    borderRadius: 0,
    minHeight: "22px",
    gap: 0,
  },
  "& .MuiTreeItem-content:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.055)",
  },
  "& .MuiTreeItem-content.Mui-selected, & .MuiTreeItem-content.Mui-selected.Mui-focused": {
    backgroundColor: "rgba(144, 202, 249, 0.20)",
  },
  "& .MuiTreeItem-content.Mui-selected:hover": {
    backgroundColor: "rgba(144, 202, 249, 0.26)",
  },
  "& .MuiTreeItem-content.Mui-focused": {
    backgroundColor: "transparent",
  },
  "& .MuiTreeItem-iconContainer": {
    width: "16px",
    minWidth: "16px",
    marginRight: 0,
    flexShrink: 0,
    "& svg": { fontSize: 18, color: "#8a8a8a" },
  },
  "& .MuiTreeItem-label": {
    paddingLeft: 0,
  },
  "& .MuiTreeItem-group": {
    marginLeft: "9px",
    paddingLeft: "6px",
    borderLeft: "1px solid rgba(255, 255, 255, 0.09)",
  },
};

const GRIP_SLOT_STYLE = {
  width: 14,
  height: 16,
  marginRight: 2,
  flexShrink: 0,
  boxSizing: "border-box",
};

const GRIP_STYLE = {
  ...GRIP_SLOT_STYLE,
  padding: 0,
  color: "#777",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  touchAction: "none",
};

/** Same width as the expand/collapse chevron so leaf rows line up with groups. */
function HierarchyLeafIcon() {
  return <span style={{ display: "block", width: 16, height: 16 }} />;
}

/** Grip handle — same DragIndicator as PlayEngine / prototyper. */
function DragGrip({ active, disabled, onPointerDown, title }) {
  return (
    <span
      data-reorder-handle=""
      title={title}
      className="proto-row-grip"
      onPointerDown={disabled ? undefined : onPointerDown}
      style={{
        ...GRIP_STYLE,
        cursor: disabled ? "default" : active ? "grabbing" : "grab",
        color: active ? "#90caf9" : "#777",
        visibility: disabled ? "hidden" : "visible",
      }}
    >
      <DragIndicatorIcon style={{ fontSize: 13 }} />
    </span>
  );
}

function FolderLabel({
  caption,
  isDropTarget,
  canDrag,
  gripEnabled,
  isActiveDrag,
  canManage,
  onGripPointerDown,
  onRename,
  onDelete,
  forceHideGrip,
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        minHeight: "22px",
      }}
    >
      <div
        data-category-drop={caption}
        data-sort-label
        style={{
          display: "flex",
          alignItems: "center",
          flex: 1,
          minWidth: 0,
          borderRadius: "3px",
          boxSizing: "border-box",
          border: isDropTarget ? "1px solid #90caf9" : "1px solid transparent",
          backgroundColor: isDropTarget ? "rgba(144, 202, 249, 0.12)" : undefined,
        }}
      >
        {forceHideGrip ? null : (
          <DragGrip
            active={isActiveDrag}
            disabled={!canDrag}
            title={canDrag ? "Drag to reorder category" : undefined}
            onPointerDown={
              gripEnabled
                ? (e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onGripPointerDown?.(e);
                }
                : undefined
            }
          />
        )}
        <FolderIcon
          style={{
            fontSize: 15,
            marginRight: "6px",
            color: FOLDER_ICON_COLOR,
            flexShrink: 0,
          }}
        />
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: "13px",
            fontWeight: 500,
            color: "#d6d6d6",
          }}
        >
          {caption}
        </span>
      </div>
      {canManage && (
        <span className="proto-cat-actions" style={{ display: "flex", flexShrink: 0, marginLeft: "4px" }}>
          <IconButton
            size="small"
            title="Rename category"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onRename?.(e);
            }}
            style={{ color: "#999", padding: 2 }}
          >
            <EditIcon style={{ fontSize: 14 }} />
          </IconButton>
          <IconButton
            size="small"
            title="Delete category"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onDelete?.(e);
            }}
            style={{ color: "#999", padding: 2 }}
          >
            <DeleteIcon style={{ fontSize: 14 }} />
          </IconButton>
        </span>
      )}
    </div>
  );
}

function ComponentLabel({
  component,
  isChild,
  canDrag,
  isActiveDrag,
  onGripPointerDown,
  forceHideGrip,
}) {
  const isSettingGroup = component.isSettingGroup === true;
  const omitGrip = forceHideGrip === true;
  const reserveHiddenGrip = !omitGrip && isSettingGroup;
  return (
    <div
      data-sort-label
      style={{ display: "flex", alignItems: "center", minHeight: "22px", flex: 1, minWidth: 0 }}
    >
      {omitGrip ? null : reserveHiddenGrip ? (
        <span aria-hidden style={GRIP_SLOT_STYLE} />
      ) : (
        <DragGrip
          active={isActiveDrag}
          disabled={!canDrag}
          title={canDrag ? "Drag to reorder or drop on a category" : undefined}
          onPointerDown={onGripPointerDown}
        />
      )}
      <img
        src={InnerComponentIcon}
        alt=""
        style={{
          width: 13,
          height: 13,
          marginRight: 6,
          flexShrink: 0,
          opacity: isSettingGroup ? 0.55 : 0.85,
        }}
      />
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontSize: "13px",
          fontWeight: 600,
          color: isSettingGroup ? "#c5c5c5" : "#e0e0e0",
        }}
      >
        {component.caption}
      </span>
    </div>
  );
}

function buildStructuralRoots(basePredefinedEntries, catalog) {
  const catalogByKey = _.keyBy(catalog || [], "key");

  const nodes = (basePredefinedEntries || []).map((entry) => {
    const detail = catalogByKey[entry.key];
    const versionComp = _.get(detail, "versionSettings.components[0]");
    const caption =
      detail?.caption ??
      entry.caption ??
      versionComp?.caption ??
      entry.id;
    const type = versionComp?.type || entry.key;
    const parentKey = _.has(entry, "parentKey") ? entry.parentKey : _.get(detail, "parentKey", null);

    return {
      id: entry.id,
      key: entry.key,
      caption,
      type,
      parentKey: parentKey || null,
      children: [],
    };
  });

  const byKey = _.keyBy(nodes, "key");
  const byId = _.keyBy(nodes, "id");
  const roots = [];

  nodes.forEach((node) => {
    if (node.parentKey && byKey[node.parentKey] && byKey[node.parentKey] !== node) {
      byKey[node.parentKey].children.push(node);
    } else {
      roots.push(node);
    }
  });

  return { roots, byId };
}

function pruneHierarchyToAllowedIds(roots, allowedIds) {
  function pruneList(nodes) {
    const next = [];
    (nodes || []).forEach((node) => {
      const children = pruneList(node.children || []);
      if (allowedIds.has(node.id)) {
        node.children = children;
        next.push(node);
      } else {
        next.push(...children);
      }
    });
    return next;
  }

  const newRoots = pruneList(roots);
  const newById = {};
  const walk = (node) => {
    newById[node.id] = node;
    (node.children || []).forEach(walk);
  };
  newRoots.forEach(walk);
  return { roots: newRoots, byId: newById };
}

function attachSettingGroupsFromVersionSettings(byId, versionSettingsComponents) {
  const relatedById = _.groupBy(versionSettingsComponents || [], "id");
  Object.keys(byId).forEach((id) => {
    const node = byId[id];
    if (!node || node.isSettingGroup) return;
    const related = relatedById[id] || [];
    const groups = related.filter((c) => c.isGroup);
    const subGroups = related.filter((c) => c.isSubGroup);
    if (groups.length === 0 && subGroups.length === 0) {
      node.settingGroups = [];
      return;
    }
    node.settingGroups = groups.map((group) => ({
      id: makeSceneEditorGroupItemId(id, group.caption),
      componentId: id,
      caption: group.caption,
      type: "Group",
      isSettingGroup: true,
      isGroup: true,
      children: subGroups
        .filter((sg) => sg.parentGroup === group.caption)
        .map((sg) => ({
          id: makeSceneEditorSubGroupItemId(id, group.caption, sg.caption),
          componentId: id,
          caption: sg.caption,
          type: "Group",
          isSettingGroup: true,
          isSubGroup: true,
          parentGroup: group.caption,
          children: [],
        })),
    }));
  });
}

function resolveFolderOrder(folders, categoriesMap) {
  const explicit = (folders || []).map(normalizeCategoryName).filter(Boolean);
  const seen = new Set(explicit);
  const inferred = [];
  Object.values(categoriesMap || {}).forEach((path) => {
    const top = normalizeCategoryName(typeof path === "string" ? path.split(":")[0] : "");
    if (top && !seen.has(top)) {
      seen.add(top);
      inferred.push(top);
    }
  });
  inferred.sort((a, b) => a.localeCompare(b));
  return [...explicit, ...inferred];
}

function sortChildrenRecursive(node, componentOrder) {
  const scope = orderKeyForParent(node.id);
  node.children = sortSiblings(node.children || [], componentOrder, scope, (c) => c.id);
  node.children.forEach((child) => sortChildrenRecursive(child, componentOrder));
}

/**
 * @param {object} props
 */
export default function containerSceneEditorHierarchy(props) {
  const [expandedItems, setExpandedItems] = useState([]);
  const isSelectionControlled = Array.isArray(props.selectedItems);
  const [uncontrolledSelectedItems, setUncontrolledSelectedItems] = useState([]);
  const selectedItems = isSelectionControlled ? props.selectedItems : uncontrolledSelectedItems;
  const setSelectedItems = isSelectionControlled
    ? (next) => {
      const value = typeof next === "function" ? next(props.selectedItems || []) : next;
      props.onSelectedItemsChange?.(value);
    }
    : setUncontrolledSelectedItems;
  const [createCategoryOpen, setCreateCategoryOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [renameCategoryState, setRenameCategoryState] = useState(null);
  const [renameCategoryName, setRenameCategoryName] = useState("");
  const [deleteCategoryState, setDeleteCategoryState] = useState(null);
  const [dragSession, setDragSession] = useState(null);
  const [categoryDropTarget, setCategoryDropTarget] = useState(null);
  const dragSessionRef = useRef(null);
  const hierarchyReadOnly = props.hierarchyReadOnly === true;
  const dragGhostRef = useRef(null);

  const baseId = _.get(props, "selectedBase._id");
  const versionId = _.get(props, "selectedVersion._id");
  const baseEntries = _.get(props, "selectedBase.predefinedGameComponents", []);

  const metadata = _.get(props, "selectedVersion.gameConfig.metadata", {}) || {};
  const categoryData = useMemo(
    () =>
      resolveEffectiveCategoryData({
        versionMetadata: metadata,
        versions: props.versions,
        defaultVersion: props.defaultVersion,
        basePredefinedEntries: baseEntries,
        selectedVersionId: versionId,
      }),
    [metadata, props.versions, props.defaultVersion, baseEntries, versionId]
  );
  const componentCategoryFolders = categoryData.componentCategoryFolders || [];
  const componentCategories = categoryData.componentCategories || {};
  const rootLayoutOrder = categoryData.rootLayoutOrder || null;
  const componentOrder = categoryData.componentOrder || {};

  const versionSettingsComponents = _.get(
    props,
    props.settingsSource === "localization"
      ? "selectedModifiedBase.localizationSettings.components"
      : "selectedModifiedBase.versionSettings.components",
    []
  );

  const { roots: structuralRoots, byId } = useMemo(() => {
    const built = buildStructuralRoots(baseEntries, props.predefinedGameComponents);
    attachSettingGroupsFromVersionSettings(built.byId, versionSettingsComponents);
    if (props.settingsSource === "localization") {
      const locIds = new Set((versionSettingsComponents || []).map((c) => c.id).filter(Boolean));
      return pruneHierarchyToAllowedIds(built.roots, locIds);
    }
    return built;
  }, [baseId, baseEntries, props.predefinedGameComponents, versionSettingsComponents, props.settingsSource]);

  const folderOrder = useMemo(
    () => resolveFolderOrder(componentCategoryFolders, componentCategories),
    [componentCategoryFolders, componentCategories]
  );

  const uncategorizedRoots = useMemo(() => {
    return structuralRoots.filter((root) => {
      const raw = componentCategories?.[root.id];
      const folderName = normalizeCategoryName(typeof raw === "string" ? raw.split(":")[0] : "");
      return !folderName;
    });
  }, [structuralRoots, componentCategories]);

  const uncategorizedIds = useMemo(() => uncategorizedRoots.map((r) => r.id), [uncategorizedRoots]);

  const effectiveRootLayoutOrder = useMemo(
    () =>
      resolveRootLayoutOrder(
        rootLayoutOrder,
        uncategorizedIds,
        folderOrder,
        componentOrder.root
      ),
    [rootLayoutOrder, uncategorizedIds, folderOrder, componentOrder.root]
  );

  const categorizedLists = useMemo(() => {
    const map = {};
    folderOrder.forEach((name) => {
      const comps = structuralRoots
        .filter((root) => {
          const raw = componentCategories?.[root.id];
          const folderName = normalizeCategoryName(typeof raw === "string" ? raw.split(":")[0] : "");
          return folderName === name;
        })
        .map((c) => _.cloneDeep(c));
      const scope = orderKeyForCategory(name);
      map[name] = sortSiblings(comps, componentOrder, scope, (c) => c.id);
      map[name].forEach((c) => sortChildrenRecursive(c, componentOrder));
    });
    return map;
  }, [folderOrder, structuralRoots, componentCategories, componentOrder]);

  const rootsByIdForLayout = useMemo(() => {
    const cloned = structuralRoots.map((c) => _.cloneDeep(c));
    cloned.forEach((c) => sortChildrenRecursive(c, componentOrder));
    return _.keyBy(cloned, "id");
  }, [structuralRoots, componentOrder]);

  useEffect(() => {
    // On load / version switch: only expand top-level category folders so the
    // tree shows root + one level (components). Setting groups stay collapsed.
    setExpandedItems(folderOrder.map((name) => `cat-${name}`));
    // folderOrder is read for the new base/version; don't re-collapse on folder edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseId, versionId]);

  function patchMetadata(partial) {
    const current = hasOwnSceneEditorCategoryData(metadata)
      ? extractSceneEditorCategoryData(metadata)
      : categoryData;
    const nextCategoryData = {
      ..._.cloneDeep(current),
      ...partial,
    };
    props.setSelectedVersionPathValue({
      path: "gameConfig.metadata",
      value: writeSceneEditorCategoryData(metadata, nextCategoryData),
    });
  }

  async function handleSetDefaultCategorization() {
    if (props.userPermissions?.update === false) {
      props.createSnackbar?.(
        "You do not have permission to update, contact your administrator.",
        "warning",
        false
      );
      return;
    }
    const defaultRecord = findDefaultVersionRecord(props.versions, props.defaultVersion);
    if (!defaultRecord) {
      props.createSnackbar?.("No default version", "warning", false);
      return;
    }
    if (defaultRecord.isLocked === true) {
      props.createSnackbar?.("The default version is locked. Unlock it to update categorization.", "warning", false);
      return;
    }
    const layout = categoryData;
    const defaultMeta = _.get(defaultRecord, "gameConfig.metadata") || {};
    const nextMetadata = writeSceneEditorCategoryData(defaultMeta, layout);
    const defaultId = String(defaultRecord._id);
    if (defaultId === String(versionId)) {
      props.setSelectedVersionPathValue({
        path: "gameConfig.metadata",
        value: nextMetadata,
      });
      props.createSnackbar?.("Default categorization saved", "success");
      return;
    }
    if (typeof props.persistVersionPathValue !== "function") {
      props.createSnackbar?.("Could not save default categorization.", "error", false);
      return;
    }
    const ok = await props.persistVersionPathValue(defaultId, "gameConfig.metadata", nextMetadata);
    if (ok) {
      props.createSnackbar?.("Default categorization saved", "success");
    } else {
      props.createSnackbar?.("Could not save default categorization.", "error", false);
    }
  }

  function handleCreateCategory() {
    if (props.userPermissions?.update === false) {
      props.createSnackbar?.(
        "You do not have permission to update, contact your administrator.",
        "warning",
        false
      );
      return;
    }
    const existing = new Set(folderOrder);
    const err = validateNewCategoryName(newCategoryName, existing);
    if (err) {
      props.createSnackbar?.(err, "warning", false);
      return;
    }
    const name = normalizeCategoryName(newCategoryName);
    const folders = [...componentCategoryFolders];
    if (!folders.includes(name)) folders.push(name);
    const layout = [...effectiveRootLayoutOrder];
    const folderEntry = rootLayoutEntryForFolder(name);
    if (!layout.includes(folderEntry)) layout.push(folderEntry);

    patchMetadata({
      componentCategoryFolders: folders,
      rootLayoutOrder: layout,
    });

    setExpandedItems((prev) => (prev.includes(`cat-${name}`) ? prev : [...prev, `cat-${name}`]));
    setCreateCategoryOpen(false);
    setNewCategoryName("");
  }

  function resolveCategoryDropTarget(clientX, clientY) {
    const el = document.elementFromPoint(clientX, clientY);
    const dropEl = el?.closest("[data-category-drop]");
    if (dropEl) return dropEl.getAttribute("data-category-drop");
    const rootZone = document.querySelector("[data-scene-editor-root-zone]");
    if (rootZone && rootZone.contains(el)) return "uncategorized";
    return null;
  }

  function getCategoryDropIntent(componentId, catTarget) {
    if (!catTarget) return null;
    const current = componentCategories[componentId] || "";
    if (catTarget === "uncategorized") return current ? "uncategorized" : null;
    if (catTarget !== current) return catTarget;
    return null;
  }

  function assignComponentCategory(componentId, categoryPath) {
    const nextCategories = _.cloneDeep(componentCategories);
    const nextOrder = _.cloneDeep(componentOrder || {});
    let nextLayout = [...effectiveRootLayoutOrder];
    const componentEntry = rootLayoutEntryForComponent(componentId);

    const prevFolder = nextCategories[componentId] || null;
    if (prevFolder) {
      const prevKey = orderKeyForCategory(prevFolder);
      if (Array.isArray(nextOrder[prevKey])) {
        nextOrder[prevKey] = nextOrder[prevKey].filter((id) => id !== componentId);
      }
    } else {
      nextLayout = nextLayout.filter((e) => e !== componentEntry);
      if (Array.isArray(nextOrder.root)) {
        nextOrder.root = nextOrder.root.filter((id) => id !== componentId);
      }
    }

    if (!categoryPath) {
      delete nextCategories[componentId];
      if (!nextLayout.includes(componentEntry)) nextLayout.push(componentEntry);
      nextOrder.root = [...(nextOrder.root || []).filter((id) => id !== componentId), componentId];
    } else {
      nextCategories[componentId] = categoryPath;
      const catKey = orderKeyForCategory(categoryPath);
      nextOrder[catKey] = [...(nextOrder[catKey] || []).filter((id) => id !== componentId), componentId];
      const folderEntry = rootLayoutEntryForFolder(categoryPath);
      if (!nextLayout.includes(folderEntry)) nextLayout.push(folderEntry);
    }

    patchMetadata({
      componentCategories: nextCategories,
      componentOrder: nextOrder,
      rootLayoutOrder: nextLayout,
      componentCategoryFolders: componentCategoryFolders,
    });
  }

  function persistRootLayoutOrder(previewIds) {
    const componentIds = [];
    const folderNames = [];
    previewIds.forEach((entry) => {
      const parsed = parseRootLayoutEntry(entry);
      if (parsed?.type === "component") componentIds.push(parsed.id);
      if (parsed?.type === "folder") folderNames.push(parsed.id);
    });
    const nextOrder = _.cloneDeep(componentOrder || {});
    nextOrder.root = componentIds;
    patchMetadata({
      rootLayoutOrder: previewIds,
      componentOrder: nextOrder,
      componentCategoryFolders: folderNames.length
        ? folderNames
        : componentCategoryFolders,
    });
  }

  function persistScopeOrder(scopeKey, previewIds) {
    const nextOrder = _.cloneDeep(componentOrder || {});
    nextOrder[scopeKey] = previewIds;
    patchMetadata({
      componentOrder: nextOrder,
    });
  }

  function beginComponentDrag(reorderScope, componentId, pointerEvent, canCategorize) {
    if (props.userPermissions?.update === false) {
      props.createSnackbar?.(
        "You do not have permission to update, contact your administrator.",
        "warning",
        false
      );
      return;
    }
    const container = document.querySelector(`[data-playEngine-sort-list="${reorderScope}"]`);
    const sortLookupId =
      reorderScope === ROOT_LAYOUT_SCOPE ? rootLayoutEntryForComponent(componentId) : componentId;
    const row =
      container?.querySelector(`[data-sort-id="${sortLookupId}"]`) ||
      pointerEvent.currentTarget.closest("[data-sort-id]");
    if (!row) return;

    const rowRect = row.getBoundingClientRect();
    const labelEl = row.querySelector("[data-sort-label]") || row;
    const labelRect = labelEl.getBoundingClientRect();

    let ids;
    if (reorderScope === ROOT_LAYOUT_SCOPE) {
      ids = [...effectiveRootLayoutOrder];
    } else if (reorderScope.startsWith("category:")) {
      const folder = reorderScope.slice("category:".length);
      ids = (categorizedLists[folder] || []).map((c) => c.id);
    } else if (reorderScope.startsWith("parent:")) {
      const parentId = reorderScope.slice("parent:".length);
      const parent = byId[parentId];
      const kids = sortSiblings(parent?.children || [], componentOrder, reorderScope, (c) => c.id);
      ids = kids.map((c) => c.id);
    } else {
      ids = [componentId];
    }

    try {
      pointerEvent.currentTarget.setPointerCapture(pointerEvent.pointerId);
    } catch (_) {
      /* ignore */
    }

    const session = {
      scopeKey: reorderScope,
      draggedId: reorderScope === ROOT_LAYOUT_SCOPE ? rootLayoutEntryForComponent(componentId) : componentId,
      componentId,
      previewIds: ids,
      initialIds: [...ids],
      pointerX: pointerEvent.clientX,
      pointerY: pointerEvent.clientY,
      grabOffsetY: pointerEvent.clientY - labelRect.top,
      canCategorize,
      floatTop: labelRect.top,
      floatLeft: rowRect.left,
      floatWidth: rowRect.width,
      slotHeight: Math.max(20, Math.round(labelRect.height)),
      pointerId: pointerEvent.pointerId,
      captureTarget: pointerEvent.currentTarget,
      caption: byId[componentId]?.caption || componentId,
    };
    dragSessionRef.current = session;
    setDragSession(session);
  }

  function beginCategoryDrag(folderName, pointerEvent) {
    if (props.userPermissions?.update === false) {
      props.createSnackbar?.(
        "You do not have permission to update, contact your administrator.",
        "warning",
        false
      );
      return;
    }
    const sortLookupId = rootLayoutEntryForFolder(folderName);
    const container = document.querySelector(`[data-playEngine-sort-list="${ROOT_LAYOUT_SCOPE}"]`);
    const row =
      container?.querySelector(`[data-sort-id="${sortLookupId}"]`) ||
      pointerEvent.currentTarget.closest("[data-sort-id]");
    if (!row) return;

    const rowRect = row.getBoundingClientRect();
    const labelEl = row.querySelector("[data-sort-label]") || row;
    const labelRect = labelEl.getBoundingClientRect();

    try {
      pointerEvent.currentTarget.setPointerCapture(pointerEvent.pointerId);
    } catch (_) {
      /* ignore */
    }

    const ids = [...effectiveRootLayoutOrder];
    const session = {
      dragKind: "category",
      scopeKey: ROOT_LAYOUT_SCOPE,
      draggedId: sortLookupId,
      name: folderName,
      categoryPath: folderName,
      previewIds: ids,
      initialIds: [...ids],
      pointerX: pointerEvent.clientX,
      pointerY: pointerEvent.clientY,
      grabOffsetY: pointerEvent.clientY - labelRect.top,
      canCategorize: false,
      floatTop: labelRect.top,
      floatLeft: rowRect.left,
      floatWidth: rowRect.width,
      slotHeight: Math.max(20, Math.round(labelRect.height)),
      pointerId: pointerEvent.pointerId,
      captureTarget: pointerEvent.currentTarget,
    };
    dragSessionRef.current = session;
    setDragSession(session);
  }

  function handleRenameCategory() {
    if (!renameCategoryState) return;
    const { categoryPath, name } = renameCategoryState;
    const trimmed = normalizeCategoryName(renameCategoryName);
    if (trimmed === name) {
      setRenameCategoryState(null);
      setRenameCategoryName("");
      return;
    }
    const siblingSet = new Set(folderOrder);
    const err = validateRenameCategoryName(renameCategoryName, siblingSet, name);
    if (err) {
      props.createSnackbar?.(err, "warning", false);
      return;
    }
    const { categories, folders, order } = renameCategoryInMeta(
      componentCategories,
      componentCategoryFolders,
      componentOrder,
      {},
      categoryPath,
      trimmed
    );
    patchMetadata({
      componentCategories: categories,
      componentCategoryFolders: folders,
      componentOrder: order,
      rootLayoutOrder: remapRootLayoutOrder(effectiveRootLayoutOrder, categoryPath, trimmed),
    });
    setExpandedItems((prev) => {
      const oldId = `cat-${categoryPath}`;
      const newId = `cat-${trimmed}`;
      return [...new Set(prev.filter((id) => id !== oldId).concat(newId))];
    });
    setRenameCategoryState(null);
    setRenameCategoryName("");
  }

  function handleDeleteCategory() {
    if (!deleteCategoryState) return;
    const { categoryPath } = deleteCategoryState;
    const { categories, folders, order } = deleteCategoryFromMeta(
      componentCategories,
      componentCategoryFolders,
      componentOrder,
      {},
      categoryPath
    );
    // Components that were in this folder become uncategorized — append to root layout.
    const removedIds = Object.entries(componentCategories || {})
      .filter(([, path]) => path === categoryPath || String(path).startsWith(`${categoryPath}:`))
      .map(([id]) => id);
    let nextLayout = pruneRootLayoutOrder(effectiveRootLayoutOrder, categoryPath) || [...effectiveRootLayoutOrder];
    const nextOrder = _.cloneDeep(order || {});
    removedIds.forEach((id) => {
      const entry = rootLayoutEntryForComponent(id);
      if (!nextLayout.includes(entry)) nextLayout.push(entry);
      nextOrder.root = [...(nextOrder.root || []).filter((x) => x !== id), id];
    });
    patchMetadata({
      componentCategories: categories,
      componentCategoryFolders: folders,
      componentOrder: nextOrder,
      rootLayoutOrder: nextLayout,
    });
    setDeleteCategoryState(null);
  }

  useEffect(() => {
    if (!dragSession) return undefined;

    function onPointerMove(e) {
      const session = dragSessionRef.current;
      if (!session) return;

      const catTarget = session.canCategorize
        ? resolveCategoryDropTarget(e.clientX, e.clientY)
        : null;
      setCategoryDropTarget((prev) => (prev === catTarget ? prev : catTarget));

      const categorizing = session.canCategorize && getCategoryDropIntent(session.componentId, catTarget);
      let previewIds = session.previewIds;
      if (!categorizing) {
        previewIds = computePreviewIdsFromPointer(
          e.clientY,
          session.scopeKey,
          session.draggedId,
          session.previewIds
        );
      }

      const next = {
        ...session,
        pointerX: e.clientX,
        pointerY: e.clientY,
        previewIds,
        floatTop: e.clientY - session.grabOffsetY,
      };
      dragSessionRef.current = next;
      setDragSession(next);
      if (dragGhostRef.current) {
        dragGhostRef.current.style.top = `${next.floatTop}px`;
      }
    }

    function commitDragSession() {
      const session = dragSessionRef.current;
      if (!session) return;
      if (session.captureTarget?.releasePointerCapture) {
        try {
          session.captureTarget.releasePointerCapture(session.pointerId);
        } catch (_) {
          /* ignore */
        }
      }

      const catIntent =
        session.canCategorize && session.dragKind !== "category"
          ? getCategoryDropIntent(
            session.componentId,
            resolveCategoryDropTarget(session.pointerX, session.pointerY)
          )
          : null;

      const orderChanged = session.previewIds && !_.isEqual(session.previewIds, session.initialIds);

      dragSessionRef.current = null;
      setCategoryDropTarget(null);
      setDragSession(null);

      if (catIntent) {
        if (catIntent === "uncategorized") {
          assignComponentCategory(session.componentId, null);
        } else {
          assignComponentCategory(session.componentId, catIntent);
          setExpandedItems((prev) =>
            prev.includes(`cat-${catIntent}`) ? prev : [...prev, `cat-${catIntent}`]
          );
        }
        return;
      }

      if (!orderChanged) return;
      if (props.userPermissions?.update === false) {
        props.createSnackbar?.(
          "You do not have permission to update, contact your administrator.",
          "warning",
          false
        );
        return;
      }

      if (session.scopeKey === ROOT_LAYOUT_SCOPE) {
        // Covers both component reorder and category folder reorder at root.
        persistRootLayoutOrder(session.previewIds);
      } else {
        persistScopeOrder(session.scopeKey, session.previewIds);
      }
    }

    window.addEventListener("pointermove", onPointerMove, { passive: true });
    window.addEventListener("pointerup", commitDragSession);
    window.addEventListener("pointercancel", commitDragSession);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", commitDragSession);
      window.removeEventListener("pointercancel", commitDragSession);
    };
  }, [dragSession?.draggedId, componentCategories, effectiveRootLayoutOrder, componentOrder]);

  function renderSettingGroupNode(group) {
    return (
      <div key={group.id}>
        <TreeItem
          itemId={group.id}
          label={
            <ComponentLabel
              component={group}
              isChild
              canDrag={false}
              forceHideGrip={hierarchyReadOnly}
            />
          }
        >
          {(group.children || []).map((child) => renderSettingGroupNode(child))}
        </TreeItem>
      </div>
    );
  }

  function renderComponentNode(component, { isChild = false, reorderScope = null, canCategorize = false, sortId = null } = {}) {
    const childScope = orderKeyForParent(component.id);
    const rowSortId = sortId || component.id;
    const isDraggedAway =
      dragSession &&
      dragSession.dragKind !== "category" &&
      dragSession.scopeKey === reorderScope &&
      (dragSession.draggedId === component.id || dragSession.draggedId === rowSortId);

    if (isDraggedAway) {
      return (
        <div
          key={component.id}
          data-sort-id={rowSortId}
          style={{
            height: dragSession.slotHeight,
            borderRadius: 2,
            border: "1px dashed rgba(144, 202, 249, 0.35)",
            backgroundColor: "rgba(144, 202, 249, 0.06)",
            boxSizing: "border-box",
          }}
          aria-hidden
        />
      );
    }

    const canDrag =
      !hierarchyReadOnly &&
      props.userPermissions?.update !== false &&
      !!reorderScope &&
      !dragSession;

    return (
      <div key={component.id} data-sort-id={rowSortId}>
        <TreeItem
          itemId={component.id}
          label={
            <ComponentLabel
              component={component}
              isChild={isChild}
              canDrag={canDrag}
              forceHideGrip={hierarchyReadOnly}
              isActiveDrag={dragSession?.componentId === component.id}
              onGripPointerDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                beginComponentDrag(reorderScope, component.id, e, canCategorize);
              }}
            />
          }
        >
          {((component.settingGroups && component.settingGroups.length > 0) ||
            (component.children && component.children.length > 0)) ? (
            <>
              {(component.settingGroups || []).map((group) => renderSettingGroupNode(group))}
              {component.children?.length > 0 ? (
                <div
                  data-playEngine-sort-list={childScope}
                  style={dragSession ? { overflowAnchor: "none" } : undefined}
                >
                  {(dragSession?.scopeKey === childScope
                    ? (dragSession.previewIds || [])
                      .map((id) => (component.children || []).find((c) => c.id === id) || byId[id])
                      .filter(Boolean)
                    : component.children
                  ).map((child) =>
                    renderComponentNode(child, {
                      isChild: true,
                      reorderScope: childScope,
                      canCategorize: false,
                    })
                  )}
                </div>
              ) : null}
            </>
          ) : null}
        </TreeItem>
      </div>
    );
  }

  function renderRootLayout() {
    const previewOrder =
      dragSession?.scopeKey === ROOT_LAYOUT_SCOPE ? dragSession.previewIds : effectiveRootLayoutOrder;

    return previewOrder.map((entry) => {
      const parsed = parseRootLayoutEntry(entry);
      if (!parsed) return null;

      if (parsed.type === "folder") {
        const folderName = parsed.id;
        const catId = `cat-${folderName}`;
        const catScope = orderKeyForCategory(folderName);
        const comps =
          dragSession?.scopeKey === catScope
            ? (dragSession.previewIds || [])
              .map((id) => (categorizedLists[folderName] || []).find((c) => c.id === id) || byId[id])
              .filter(Boolean)
            : categorizedLists[folderName] || [];
        if (hierarchyReadOnly && comps.length === 0) return null;
        const isCatDraggedAway =
          dragSession?.dragKind === "category" &&
          dragSession?.scopeKey === ROOT_LAYOUT_SCOPE &&
          dragSession?.draggedId === entry;

        if (isCatDraggedAway) {
          return (
            <div
              key={catId}
              data-sort-id={entry}
              style={{
                height: dragSession.slotHeight,
                margin: "2px 4px",
                borderRadius: 2,
                border: "1px dashed rgba(144, 202, 249, 0.35)",
                backgroundColor: "rgba(144, 202, 249, 0.06)",
              }}
              aria-hidden
            />
          );
        }

        const canManage = !hierarchyReadOnly && props.userPermissions?.update !== false;

        return (
          <div key={catId} data-sort-id={entry}>
            <TreeItem
              itemId={catId}
              label={
                <FolderLabel
                  caption={folderName}
                  isDropTarget={!hierarchyReadOnly && categoryDropTarget === folderName}
                  canDrag={canManage}
                  forceHideGrip={hierarchyReadOnly}
                  gripEnabled={canManage && !dragSession}
                  isActiveDrag={dragSession?.dragKind === "category" && dragSession?.name === folderName}
                  canManage={canManage}
                  onGripPointerDown={(e) => beginCategoryDrag(folderName, e)}
                  onRename={() => {
                    setRenameCategoryName(folderName);
                    setRenameCategoryState({ categoryPath: folderName, name: folderName });
                  }}
                  onDelete={() => {
                    setDeleteCategoryState({ categoryPath: folderName, name: folderName });
                  }}
                />
              }
            >
              <div
                data-playEngine-sort-list={catScope}
                data-category-drop={folderName}
                style={{
                  minHeight: 8,
                  ...(dragSession ? { overflowAnchor: "none" } : {}),
                }}
              >
                {comps.map((comp) =>
                  renderComponentNode(comp, {
                    reorderScope: catScope,
                    canCategorize: true,
                  })
                )}
              </div>
            </TreeItem>
          </div>
        );
      }

      const comp = rootsByIdForLayout[parsed.id];
      if (!comp) return null;
      return renderComponentNode(comp, {
        reorderScope: ROOT_LAYOUT_SCOPE,
        canCategorize: true,
        sortId: entry,
      });
    });
  }

  const hierarchyCaption = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        padding: "8px 12px",
        backgroundColor: BAR_BG,
        borderTop: `1px solid ${SECTION_DIVIDER}`,
        borderBottom: `1px solid ${SECTION_DIVIDER}`,
        flexShrink: 0,
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 600, color: "#e8e8e8" }}>Hierarchy</span>
    </div>
  );

  if (!baseEntries || baseEntries.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          backgroundColor: PANEL_BG,
          boxSizing: "border-box",
        }}
      >
        {hierarchyCaption}
        <div style={{ padding: 16 }}>
          <Typography variant="body2" style={{ color: "#777" }}>
            No components listed on this base.
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        backgroundColor: PANEL_BG,
        overflow: "hidden",
      }}
    >
      {hierarchyCaption}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "4px 0" }}>
        {!hierarchyReadOnly ? (
          <div style={{ display: "flex", justifyContent: "flex-start", gap: 6, padding: "4px 5px 4px 10px" }}>
            <Button
              size="small"
              disabled={props.userPermissions?.update === false}
              onClick={() => {
                setNewCategoryName("");
                setCreateCategoryOpen(true);
              }}
              startIcon={<AddIcon style={{ fontSize: 14 }} />}
              style={{
                color: "#aaa",
                fontSize: "11px",
                textTransform: "none",
                minWidth: 0,
                padding: "2px 8px",
                backgroundColor: BAR_BG,
                borderRadius: 4,
              }}
            >
              Category
            </Button>
            <Button
              size="small"
              disabled={props.userPermissions?.update === false}
              onClick={() => {
                handleSetDefaultCategorization();
              }}
              style={{
                color: "#aaa",
                fontSize: "11px",
                textTransform: "none",
                minWidth: 0,
                padding: "2px 8px",
                backgroundColor: BAR_BG,
                borderRadius: 4,
              }}
            >
              Set Default Categorization
            </Button>
          </div>
        ) : null}
        <SimpleTreeView
          aria-label="scene-editor-hierarchy"
          expandedItems={expandedItems}
          onExpandedItemsChange={(event, itemIds) => setExpandedItems(itemIds)}
          selectedItems={selectedItems}
          onSelectedItemsChange={(event, itemIds) => {
            // Keep component ids only — category folders are not editable targets.
            const raw = itemIds == null ? [] : Array.isArray(itemIds) ? itemIds : [itemIds];
            const next = raw.filter((id) => id && !String(id).startsWith("cat-"));
            setSelectedItems(next);
          }}
          expansionTrigger="iconContainer"
          slots={{
            expandIcon: ArrowRightIcon,
            collapseIcon: ArrowDropDownIcon,
            endIcon: HierarchyLeafIcon,
          }}
          sx={TREE_SX}
        >
          <div
            data-scene-editor-root-zone
            data-category-drop="uncategorized"
            data-playEngine-sort-list={ROOT_LAYOUT_SCOPE}
            style={{
              minHeight: dragSession?.canCategorize ? 40 : undefined,
              margin: "2px 4px 4px",
              borderRadius: 2,
              boxSizing: "border-box",
              border:
                categoryDropTarget === "uncategorized"
                  ? "1px solid #90caf9"
                  : "1px solid transparent",
              backgroundColor:
                categoryDropTarget === "uncategorized"
                  ? "rgba(144, 202, 249, 0.06)"
                  : "transparent",
              ...(dragSession ? { overflowAnchor: "none" } : {}),
            }}
          >
            {renderRootLayout()}
          </div>
        </SimpleTreeView>
      </div>

      {dragSession &&
        ReactDOM.createPortal(
          <div
            ref={dragGhostRef}
            style={{
              position: "fixed",
              top: dragSession.floatTop,
              left: dragSession.floatLeft,
              width: dragSession.floatWidth,
              height: dragSession.slotHeight,
              zIndex: 10000,
              pointerEvents: "none",
              borderRadius: 2,
              backgroundColor: BAR_BG,
              border: "1px solid rgba(144, 202, 249, 0.55)",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              padding: "0 6px",
            }}
          >
            <DragIndicatorIcon style={{ fontSize: 14, color: "#90caf9", marginRight: 2, flexShrink: 0 }} />
            {dragSession.dragKind === "category" ? (
              <>
                <FolderIcon style={{ fontSize: 15, color: FOLDER_ICON_COLOR, marginRight: 6, flexShrink: 0 }} />
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#d6d6d6",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                  }}
                >
                  {dragSession.name}
                </span>
              </>
            ) : (
              <>
                <img
                  src={InnerComponentIcon}
                  alt=""
                  style={{ width: 13, height: 13, marginRight: 6, flexShrink: 0, opacity: 0.85 }}
                />
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#e0e0e0",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {dragSession.caption}
                </span>
              </>
            )}
          </div>,
          document.body
        )}

      <Dialog
        open={createCategoryOpen}
        onClose={() => setCreateCategoryOpen(false)}
        PaperProps={{ style: { backgroundColor: CARD_BG, minWidth: 320 } }}
      >
        <DialogTitle style={{ color: "#eee" }}>Create category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Category name"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreateCategory();
            }}
            InputProps={{ style: { color: "#fff" } }}
            InputLabelProps={{ style: { color: "#888" } }}
            helperText="Single folder name — no colons"
            FormHelperTextProps={{ style: { color: "#666" } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateCategoryOpen(false)} style={{ color: "#aaa" }}>
            Cancel
          </Button>
          <Button onClick={handleCreateCategory} color="primary" variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!renameCategoryState}
        onClose={() => {
          setRenameCategoryState(null);
          setRenameCategoryName("");
        }}
        PaperProps={{ style: { backgroundColor: CARD_BG, minWidth: 320 } }}
      >
        <DialogTitle style={{ color: "#eee" }}>Rename category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Category name"
            value={renameCategoryName}
            onChange={(e) => setRenameCategoryName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleRenameCategory();
            }}
            InputProps={{ style: { color: "#fff" } }}
            InputLabelProps={{ style: { color: "#888" } }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRenameCategoryState(null);
              setRenameCategoryName("");
            }}
            style={{ color: "#aaa" }}
          >
            Cancel
          </Button>
          <Button onClick={handleRenameCategory} color="primary" variant="contained">
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deleteCategoryState}
        onClose={() => setDeleteCategoryState(null)}
        PaperProps={{ style: { backgroundColor: CARD_BG, minWidth: 320 } }}
      >
        <DialogTitle style={{ color: "#eee" }}>Delete category</DialogTitle>
        <DialogContent>
          <Typography style={{ color: "#ccc", fontSize: 14 }}>
            Delete &ldquo;{deleteCategoryState?.name}&rdquo;? Components inside will become uncategorized.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCategoryState(null)} style={{ color: "#aaa" }}>
            Cancel
          </Button>
          <Button onClick={handleDeleteCategory} style={{ color: "#ef9a9a" }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
